import React from "react";
import { Box, Typography, Drawer, List, IconButton, Button, Divider, styled } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import CartItem from "../CartItem/CartItem";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../app/store";
import {
  cartLineKey,
  clearCart,
  removeItemFromCart,
  updateCartItem,
} from "../../services/cartSlice/cartSlice";
import BuyButton from "../BuyButton/BuyButton";
import CartTotals from "../CartTotals/CartTotals";
import { gaEvent, gaValue, toGaItem } from "../../analytics";
import { CART_TEXTS } from "../../pages/shared/constants";

const DRAWER_MAX_WIDTH = 600;
const NAV_DELAY_MS = 300;

const CartDrawerBackground = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  background: theme.palette.text.primary,
}));

const CartDrawerContent = styled(Box)(({ theme }) => ({
  maxWidth: DRAWER_MAX_WIDTH,
  width: "100%",
  background: theme.palette.background.default,
  padding: theme.spacing(1.25),
  borderRadius: theme.spacing(0.5),
}));

const CartDrawerHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(2),
}));

/**
 * Stacked, not side by side (EB-556). The two actions here are not peers: one goes to checkout and
 * the other throws the cart away. Sitting them in a row at equal width said they were.
 */
const Actions = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(2),
}));

/** Reachable, quiet, and reversible only by re-adding everything — so it gets no fill at all. */
const EmptyCartButton = styled(Button)(({ theme }) => ({
  alignSelf: "center",
  textTransform: "none",
  fontWeight: 700,
  color: theme.palette.error.main,
}));

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

const CartComponent: React.FC<CartDrawerProps> = ({ open, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const cartItems = useSelector((state: RootState) => state.cart.items);

  const updateCartQuantity = (key: string, newQuantity: number) => {
    dispatch(updateCartItem({ key, quantity: newQuantity }));
  };

  const removeCartItem = (key: string) => {
    const item = cartItems.find((i) => cartLineKey(i) === key);
    if (item) {
      gaEvent("remove_from_cart", {
        currency: "COP",
        value: gaValue([item]),
        items: [toGaItem(item, { quantity: item.quantity })],
      });
    }
    dispatch(removeItemFromCart(key));
  };

  const emptyCart = () => {
    gaEvent("clear_cart");
    dispatch(clearCart());
  };

  const itemsSubtotal = useSelector((state: RootState) => state.cart.itemsSubtotal);
  const quoteItemCount = useSelector((state: RootState) => state.cart.quoteItemCount);
  const navigate = useNavigate();

  const handlePurchase = () => {
    // `itemsSubtotal` is already the discounted subtotal the shopper is looking at, so it stays —
    // it is the cart's own arithmetic and `gaValue` would only be a second opinion on it. The
    // lines go through the shared builder so their prices agree with that total (EB-558).
    gaEvent("begin_checkout", {
      currency: "COP",
      value: itemsSubtotal,
      items: cartItems.map((item) => toGaItem(item, { quantity: item.quantity })),
    });
    onClose();
    setTimeout(() => navigate("/hacer-pedido"), NAV_DELAY_MS);
  };

  return (
    <Drawer anchor="bottom" open={open} onClose={onClose}>
      <CartDrawerBackground>
        <CartDrawerContent>
          <CartDrawerHeader>
            <Typography variant="h6" fontWeight={"bold"}>
              Carrito de compras
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </CartDrawerHeader>

          <List>
            {cartItems.map((item) => (
              <CartItem
                key={cartLineKey(item)}
                item={item}
                updateCartItem={updateCartQuantity}
                removeCartItem={removeCartItem}
              />
            ))}
          </List>
          <Divider sx={{ my: 2 }} />
          <CartTotals itemsSubtotal={itemsSubtotal} quoteItemCount={quoteItemCount} />
          <Actions>
            <BuyButton handlePurchase={handlePurchase} disabled={cartItems.length === 0} />
            <EmptyCartButton
              variant="text"
              size="small"
              onClick={emptyCart}
              disabled={cartItems.length === 0}
            >
              {CART_TEXTS.empty}
            </EmptyCartButton>
          </Actions>
        </CartDrawerContent>
      </CartDrawerBackground>
    </Drawer>
  );
};

export default CartComponent;
